import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiX, FiTool, FiClipboard, FiTruck, FiFileText, FiBell } from 'react-icons/fi';

function SupportComingSoon({ isOpen, onClose, onServiceRequestClick }) {
  const { t } = useTranslation();
  
  // Ref to track if we're transitioning to another modal (not actually closing)
  const isTransitioningRef = useRef(false);

  // Ref to track if cleanup should actually restore scroll
  const shouldRestoreScrollRef = useRef(true);
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      shouldRestoreScrollRef.current = true;
      
      // Get scroll position - use window global if set, otherwise window.scrollY
      let scrollY;
      if (window.__modalScrollY !== undefined) {
        scrollY = window.__modalScrollY;
      } else {
        scrollY = window.scrollY;
        window.__modalScrollY = scrollY;
      }
      
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        shouldRestoreScrollRef.current = false;
        
        // Only restore scroll position if NOT transitioning to another modal
        if (!isTransitioningRef.current) {
          // Defer the actual cleanup to next tick to handle StrictMode
          setTimeout(() => {
            if (!shouldRestoreScrollRef.current && window.__modalScrollY !== undefined) {
              const savedScrollY = window.__modalScrollY;
              delete window.__modalScrollY;
              document.body.style.position = '';
              document.body.style.top = '';
              document.body.style.left = '';
              document.body.style.right = '';
              document.body.style.overflow = '';
              window.scrollTo(0, savedScrollY);
            }
          }, 0);
        }
        // Reset the transitioning flag
        isTransitioningRef.current = false;
      };
    }
  }, [isOpen]);

  // Get translated content
  const title = t('supportComingSoon.title', { defaultValue: 'Service & Support Center' });
  const comingSoon = t('supportComingSoon.comingSoon', { defaultValue: 'Coming Soon' });
  const intro = t('supportComingSoon.intro', { defaultValue: 'HADEN has delivered 50+ engineered systems across multiple defense programs. This portal will provide our customers with:' });
  const footerNote = t('supportComingSoon.footerNote', { defaultValue: 'This portal is currently under development. For immediate support, please contact us via the inquiry form or email.' });
  const serviceRequestCta = t('supportComingSoon.serviceRequestCta', { defaultValue: 'Submit Service Request' });

  // Get translated features
  const defaultFeatures = [
    { icon: 'tool', title: 'Service Request Submission', description: 'Log maintenance or support tickets' },
    { icon: 'clipboard', title: 'Complaint Lodging', description: 'Report issues with delivered systems' },
    { icon: 'truck', title: 'Tracking History', description: 'View status of your service requests' },
    { icon: 'file', title: 'Documentation Access', description: 'Retrieve system manuals & certificates' },
    { icon: 'bell', title: 'Maintenance Alerts', description: 'Scheduled service notifications' }
  ];

  const translatedFeatures = t('supportComingSoon.features', { returnObjects: true });
  const features = Array.isArray(translatedFeatures) ? translatedFeatures : defaultFeatures;

  const iconMap = {
    tool: FiTool,
    clipboard: FiClipboard,
    truck: FiTruck,
    file: FiFileText,
    bell: FiBell
  };

  const handleServiceRequestClick = () => {
    // Set flag to indicate we're transitioning to another modal
    // This prevents scroll restoration in the cleanup
    isTransitioningRef.current = true;
    onClose();
    if (onServiceRequestClick) {
      onServiceRequestClick();
    }
  };

  // Use portal to render modal at document body level
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 10000
            }}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={onClose}
            className="fixed inset-0 flex items-start md:items-center justify-center pt-4 md:pt-0 px-4 pb-4"
            style={{ zIndex: 10001 }}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto"
              style={{ overscrollBehavior: 'contain' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div 
                className="px-6 py-5 border-b"
                style={{ borderColor: 'rgba(16, 25, 87, 0.1)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h2 
                      className="text-xl font-bold"
                      style={{ color: '#101957' }}
                    >
                      {title}
                    </h2>
                    <span 
                      className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full"
                      style={{ 
                        backgroundColor: 'rgba(16, 25, 87, 0.08)',
                        color: '#101957'
                      }}
                    >
                      {comingSoon}
                    </span>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <FiX className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5">
                {/* Intro */}
                <p 
                  className="text-sm leading-relaxed mb-5"
                  style={{ color: '#475569' }}
                >
                  {intro}
                </p>

                {/* Planned Features */}
                <div className="space-y-3">
                  {features.map((feature, index) => {
                    const Icon = iconMap[feature.icon] || FiTool;
                    return (
                      <div 
                        key={index}
                        className="flex items-start p-3 rounded-lg"
                        style={{ backgroundColor: '#F8FAFC' }}
                      >
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mr-3"
                          style={{ backgroundColor: 'rgba(16, 25, 87, 0.08)' }}
                        >
                          <Icon className="w-4 h-4" style={{ color: '#101957' }} />
                        </div>
                        <div>
                          <h4 
                            className="text-sm font-semibold"
                            style={{ color: '#0B1220' }}
                          >
                            {feature.title}
                          </h4>
                          <p 
                            className="text-xs mt-0.5"
                            style={{ color: '#64748b' }}
                          >
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer Note */}
                <p 
                  className="text-xs mt-5 p-3 rounded-lg text-center italic"
                  style={{ 
                    backgroundColor: 'rgba(16, 25, 87, 0.04)',
                    color: '#64748b'
                  }}
                >
                  {footerNote}
                </p>
              </div>

              {/* CTA */}
              <div 
                className="px-6 py-4 border-t"
                style={{ borderColor: 'rgba(16, 25, 87, 0.1)' }}
              >
                <button
                  onClick={handleServiceRequestClick}
                  className="w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all hover:opacity-90"
                  style={{ 
                    backgroundColor: '#101957',
                    color: '#ffffff'
                  }}
                >
                  {serviceRequestCta}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // Render modal using portal to body
  return ReactDOM.createPortal(modalContent, document.body);
}

export default SupportComingSoon;
